import * as d3 from "d3";
import ImageCropperGenerator from "./ImageCropperGenerator";

export default class NodesGenerator {
	// Public:
	constructor(cParams) {
		this.saveParams(cParams);
		this.createFillFunction();
	}

	compute(nodes) {
		this._nodes = nodes;
		this.createBasicFunction();
		this.appendCircleFunction();
		this.appendLabelFunction();
		this.appendTitleFunction();
		return this._func;
	}

	getLabelFadingFunction() {
		const threshold = this.getLabelFadingThreshold();
		const transitionTime = this._textFadingTime;
		const elementId = this._elementId;
		let func = function (zoomManager) {
			const state = zoomManager.getZoomState();
			const state0 = zoomManager.getPreviousZoomState();
			const element = document.getElementById(elementId);
			const opacity = element.labelsHighlightingOpacityFunction;
			const labelsShouldOn = state.k > threshold;
			const labelsWereOn = state0.k > threshold;
			const shallDrawLabels = labelsShouldOn && !labelsWereOn;
			const shallRemoveLabels = !labelsShouldOn && labelsWereOn;
			const shallChange = shallDrawLabels || shallRemoveLabels;
			if (shallChange)
				var labels = d3
					.select("#" + elementId)
					.select("svg")
					.selectAll("text");
			if (shallDrawLabels) {
				labels.transition(transitionTime).style("opacity", opacity);
			} else if (shallRemoveLabels) {
				labels.transition(transitionTime).style("opacity", 0);
			}
		};
		return func;
	}

	// Private:
	saveParams(cParams) {
		this._elementId = cParams.elementId;
		this._svg = cParams.svg;
		this._dragFunc = cParams.dragFunc;
		this._masterRadius = cParams.masterRadius;
		this._borderThickness = cParams.border["thickness"];
		this._borderColor = cParams.border["color"];
		this._borderOpacity = cParams.border["opacity"];
		this._imageScalingFactor = cParams.imageScalingFactor;
		this._defaultImage = cParams.defaultImage;
		this._opacity = cParams.opacity;
		this.saveTextParams(cParams.textSettings);
		this._onclickFunc = cParams.onclickFunc;
	}

	saveTextParams(cParams) {
		this._textFont = cParams.font;
		this._textSize = cParams.size;
		this._textMinSize = cParams.minSize;
		this._textFadingTime = cParams.fadingTransitionTime;
		this._textColor = cParams.color;
		this._textOpacity = cParams.opacity;
		this._titlesEnabled = cParams.titlesEnabled;
		this._showMasterNodesLabels = cParams.showMasterNodesLabels;
		this._showSlaveNodesLabels = cParams.showSlaveNodesLabels;
		this._textSelection = cParams.userSelect;
	}

	createFillFunction() {
		this.createCroppedImageFunction();
		const imageFunc = this._imageFunc;
		const colorFunc = d => d.color;
		const func = this.createNodeTypeDiscriminationFunctionForFunctions(imageFunc, colorFunc);
		this._fillFunc = func;
	}

	createCroppedImageFunction() {
		let cParams = {
			svg: this._svg,
			imageScalingFactor: this._imageScalingFactor,
			defaultImage: this._defaultImage,
		};
		let generator = new ImageCropperGenerator(cParams);
		let func = generator.compute();
		this._imageFunc = func;
	}

	createBasicFunction() {
		let svg = this._svg;
		let nodes = this._nodes;
		let func = svg
			.append("g")
			.attr("class", "nodes")
			.selectAll("g")
			.data(nodes)
			.enter()
			.append("g");
		this._func = func;
	}

	appendCircleFunction() {
		let dragFunc = this._dragFunc;
		let onclickFunc = this._onclickFunc;
		let fillFunc = this._fillFunc;
		let opacityFunc = this.createNodeTypeDiscriminationFunctionForValues(1.0, this._opacity);
		let thickness = this._borderThickness;
		let borderColor = this._borderColor;
		let borderOpacity = this._borderOpacity;

		this._func
			.append("circle")
			.attr("r", d => d.radius)
			.attr("fill", fillFunc)
			.style("opacity", opacityFunc)
			.attr("stroke", borderColor)
			.attr("stroke-width", thickness)
			.attr("stroke-opacity", borderOpacity)
			.style("cursor", "pointer")
			.call(dragFunc)
			.on("click", onclickFunc);
	}

	appendLabelFunction() {
		const elementId = this._elementId;
		const element = document.getElementById(elementId);
		let font = this._textFont;
		let size = this._textSize;
		let color = this._textColor;
		let opacity = d => this.getLabelsOpacity(d);
		let userSelect = this._textSelection;
		let dragFunc = this._dragFunc;
		let onclickFunc = this._onclickFunc;
		const showMasterNodesLabels = this._showMasterNodesLabels
		const showSlaveNodesLabels = this._showSlaveNodesLabels
		element.labelsOpacityFunction = opacity;

		this._func
			.append("text")
			.text(d => {
				switch (d.type) {
					case "master":
						return (showMasterNodesLabels && d.label) || null
					case "slave":
						return (showSlaveNodesLabels && d.label) || null
					default:
						return d.label
				}
			})
			.attr("font-family", font)
			.attr("font-size", size)
			.attr("fill", color)
			.attr("text-anchor", "middle")
			.style("opacity", opacity)
			.style("user-select", userSelect)
			.style("cursor", "pointer")
			.call(dragFunc)
			.on("click", onclickFunc);
	}

	appendTitleFunction() {
		if (this._titlesEnabled) {
			this._func.append("title").text(d => d.label);
		}
	}

	createNodeTypeDiscriminationFunctionForValues(masterValue, slaveValue) {
		let func = function (d) {
			const type = d.type;
			switch (type) {
				case "master":
					return masterValue;
				case "slave":
					return slaveValue;
				default:
					throw Error("Invalid node type: " + type);
			}
		};
		return func;
	}

	createNodeTypeDiscriminationFunctionForFunctions(masterFunction, slaveFunction) {
		let func = function (d) {
			const type = d.type;
			switch (type) {
				case "master":
					return masterFunction(d);
				case "slave":
					return slaveFunction(d);
				default:
					throw Error("Invalid node type: " + type);
			}
		};
		return func;
	}

	getLabelFadingThreshold() {
		const minFontSize = this._textMinSize;
		const normalFontSize = this._textSize;
		const threshold = minFontSize / normalFontSize;
		return threshold;
	}

	getLabelsOpacity(d) {
		const zoomState = this.getZoomState();
		const threshold = this.getLabelFadingThreshold();
		const scale = zoomState.k;
		const opacity = this._textOpacity;
		if (scale > threshold) {
			return opacity;
		} else return 0;
	}

	getZoomState() {
		const elementId = this._elementId;
		let chart = document.getElementById(elementId);
		let zoomState = chart.__zoom;
		return zoomState;
	}
}